import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const TwoFactorSetup = ({ open, onOpenChange, user, onUpdate }) => {
  const [step, setStep] = useState('initial'); // initial, setup, verify, disable
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const is2faEnabled = user?.totp_enabled;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/2fa/setup`);
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep('setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/2fa/verify`, { code });
      toast.success('Two-factor authentication enabled!');
      setStep('initial');
      setCode('');
      onUpdate?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/2fa/disable`, { code });
      toast.success('Two-factor authentication disabled');
      setStep('initial');
      setCode('');
      onUpdate?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setStep('initial');
    setCode('');
    setQrCode('');
    setSecret('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-600" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            {is2faEnabled
              ? 'Your account is protected with 2FA.'
              : 'Add an extra layer of security to your account.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'initial' && (
          <div className="space-y-4 pt-2">
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              is2faEnabled
                ? 'bg-green-50 border-green-200'
                : 'bg-zinc-50 border-zinc-200'
            }`}>
              {is2faEnabled ? (
                <ShieldCheck className="h-6 w-6 text-green-600" />
              ) : (
                <ShieldOff className="h-6 w-6 text-zinc-400" />
              )}
              <div>
                <div className="font-medium text-sm">
                  {is2faEnabled ? '2FA is enabled' : '2FA is not enabled'}
                </div>
                <div className="text-xs text-zinc-500">
                  {is2faEnabled
                    ? 'Your account requires a verification code at login'
                    : 'Enable to require a code from Google Authenticator at login'}
                </div>
              </div>
            </div>

            {is2faEnabled ? (
              <Button
                variant="outline"
                className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setStep('disable')}
                data-testid="disable-2fa-button"
              >
                <ShieldOff className="h-4 w-4 mr-2" />
                Disable 2FA
              </Button>
            ) : (
              <Button
                className="w-full"
                onClick={handleSetup}
                disabled={loading}
                data-testid="enable-2fa-button"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </Button>
            )}
          </div>
        )}

        {step === 'setup' && (
          <form onSubmit={handleVerify} className="space-y-4 pt-2">
            <div className="text-sm text-zinc-600">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </div>

            {qrCode && (
              <div className="flex justify-center p-4 bg-white rounded-lg border border-zinc-200">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
            )}

            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Or enter this key manually:</Label>
              <div className="flex gap-2">
                <Input
                  value={secret}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="2fa-secret-key"
                />
                <Button type="button" variant="outline" size="sm" onClick={copySecret}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="verify-code">Enter the 6-digit code to verify:</Label>
              <Input
                id="verify-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                className="mt-2 text-center text-xl tracking-[0.4em] font-mono"
                data-testid="2fa-verify-code-input"
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('initial')}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={loading || code.length !== 6} data-testid="2fa-confirm-enable">
                {loading ? 'Verifying...' : 'Enable 2FA'}
              </Button>
            </div>
          </form>
        )}

        {step === 'disable' && (
          <form onSubmit={handleDisable} className="space-y-4 pt-2">
            <div className="text-sm text-zinc-600">
              Enter your current authenticator code to disable 2FA:
            </div>

            <div>
              <Label htmlFor="disable-code">Verification Code</Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                className="mt-2 text-center text-xl tracking-[0.4em] font-mono"
                data-testid="2fa-disable-code-input"
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setStep('initial'); setCode(''); }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                className="flex-1"
                disabled={loading || code.length !== 6}
                data-testid="2fa-confirm-disable"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSetup;
